import { useEffect, useState } from 'react';
import { apiJson } from '../api/client.js';
import { FormInput } from './FormControls.jsx';
import { RecoveryCodeList } from './TwoFactorSetupForm.jsx';
import { profileUi } from '../i18n/profileUi.js';
import { t } from '../i18n/translate.js';

export function TwoFactorProfileSection({ locale }) {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    apiJson('/api/profile/two-factor')
      .then((data) => setStatus(data))
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    load();
  }, []);

  const startSetup = async () => {
    setError('');
    setMsg('');
    setRecoveryCodes([]);
    setBusy(true);
    try {
      const data = await apiJson('/api/profile/two-factor/setup', { method: 'POST' });
      setSetup(data);
      setCode('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    setBusy(true);
    try {
      const data = await apiJson('/api/profile/two-factor/confirm', {
        method: 'POST',
        body: { code },
      });
      setSetup(null);
      setCode('');
      setRecoveryCodes(data.recovery_codes || []);
      setStatus({ enabled: true, enforced: data.enforced, can_disable: data.can_disable });
      setMsg(t(profileUi.twoFactorEnabledMsg, locale));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    setBusy(true);
    try {
      const data = await apiJson('/api/profile/two-factor/disable', {
        method: 'POST',
        body: { current_password: password },
      });
      setPassword('');
      setRecoveryCodes([]);
      setStatus(data);
      setMsg(t(profileUi.twoFactorDisabledMsg, locale));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    setBusy(true);
    try {
      const data = await apiJson('/api/profile/two-factor/recovery-codes', {
        method: 'POST',
        body: { current_password: password },
      });
      setPassword('');
      setRecoveryCodes(data.recovery_codes || []);
      setMsg(t(profileUi.twoFactorRecoveryReady, locale));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="auth-card flat stack" style={{ marginTop: '1rem' }} data-tour="profile-two-factor">
      <h3>{t(profileUi.twoFactorTitle, locale)}</h3>
      <p className="muted small" style={{ margin: 0 }}>
        {t(profileUi.twoFactorHint, locale)}
      </p>
      {error ? <p className="err">{error}</p> : null}
      {msg ? <p className="muted">{msg}</p> : null}
      {status ? (
        <p className="muted small" style={{ margin: 0 }}>
          {status.enabled ? t(profileUi.twoFactorOn, locale) : t(profileUi.twoFactorOff, locale)}
          {status.enforced ? ` ${t(profileUi.twoFactorRequiredNote, locale)}` : ''}
        </p>
      ) : null}
      {recoveryCodes.length > 0 ? (
        <>
          <p className="muted small" style={{ margin: 0 }}>
            {t(profileUi.twoFactorRecoveryHint, locale)}
          </p>
          <RecoveryCodeList codes={recoveryCodes} />
        </>
      ) : null}
      {!status?.enabled && !setup ? (
        <button type="button" className="btn-primary" disabled={busy} onClick={() => void startSetup()}>
          {t(profileUi.twoFactorEnable, locale)}
        </button>
      ) : null}
      {setup ? (
        <form onSubmit={confirmSetup} className="stack">
          {setup.qr_data_uri ? (
            <img className="two-factor-qr" src={setup.qr_data_uri} alt={t(profileUi.twoFactorQr, locale)} />
          ) : null}
          <p className="muted small" style={{ margin: 0 }}>
            {t(profileUi.twoFactorManual, locale)}
          </p>
          <p className="two-factor-secret">{setup.secret}</p>
          <label className="field">
            <span>{t(profileUi.twoFactorCode, locale)}</span>
            <FormInput
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </label>
          <div className="row-actions">
            <button type="button" className="btn-secondary" onClick={() => setSetup(null)}>
              {t(profileUi.twoFactorCancel, locale)}
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {t(profileUi.twoFactorConfirm, locale)}
            </button>
          </div>
        </form>
      ) : null}
      {status?.enabled ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const action = e.nativeEvent.submitter?.getAttribute('name');
            if (action === 'disable') {
              void disable(e);
              return;
            }
            void regenerate(e);
          }}
          className="stack"
        >
          <label className="field">
            <span>{t(profileUi.currentPassword, locale)}</span>
            <FormInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <div className="row-actions">
            {status.can_disable ? (
              <button type="submit" name="disable" className="btn-secondary" disabled={busy}>
                {t(profileUi.twoFactorDisable, locale)}
              </button>
            ) : null}
            <button type="submit" name="regenerate" className="btn-secondary" disabled={busy}>
              {t(profileUi.twoFactorRegenerate, locale)}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
